import {useCallback, useState} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import {useDispatch} from 'react-redux';
import {getProfile} from '../slice/profileSlice';
import {getSavedOnboardingAnswers} from '../services/onboarding-service';

// Used until the user's own fuel is known (or if it never is).
export const DEFAULT_FUEL_TYPE = 'petrol';

// The fuel the user chose at onboarding / in Profile is stored as:
//   'benzine' | 'diesel' | 'electric' | 'lpg'
// maps/gas-stations-by-fuel only accepts:
//   'petrol' | 'diesel' | 'lpg' | 'cng' | 'ev'
// This turns the first into the second. Unknown or empty text gives null.
export const toApiFuelType = value => {
  const v = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!v) {
    return null;
  }
  if (v.includes('diesel')) {
    return 'diesel';
  }
  if (v.includes('lpg')) {
    return 'lpg';
  }
  if (v.includes('cng')) {
    return 'cng';
  }
  if (v === 'ev' || v.includes('electric') || v.includes('elektr')) {
    return 'ev';
  }
  if (v.includes('benz') || v.includes('petrol') || v.includes('gasoline')) {
    return 'petrol';
  }
  return null;
};

// The fuel to search for, in the format maps/gas-stations-by-fuel expects.
// Read every time the screen comes into focus, so a change made in Profile
// is used straight away.
// Order: the copy kept on this phone (instant), then the server's get-profile
// (answers.fuel_type, then fuelPreference, then answer_2). The phone
// copy wins when it has a value; the server fills in on a new phone or the web app.
export const useUserFuelType = () => {
  const dispatch = useDispatch();
  const [fuelType, setFuelType] = useState(DEFAULT_FUEL_TYPE);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      const apply = raw => {
        const mapped = toApiFuelType(raw);
        if (mapped && active) {
          setFuelType(mapped);
        }
      };

      (async () => {
        try {
          const saved = await getSavedOnboardingAnswers();
          if (toApiFuelType(saved?.answer_2)) {
            // The phone copy is the latest choice (same rule as Profile)
            apply(saved.answer_2);
            return;
          }
        } catch (e) {
          // No copy on this phone
        }
        try {
          const result = await dispatch(getProfile()).unwrap();
          // get-profile answers {Success, Data: {...}}; the slice may hand
          // back Data itself or the whole answer, so accept both.
          const profile = result?.Data ?? result;
          // answers.fuel_type is the fuel chosen at onboarding ('benzine' ...),
          // the one Profile changes. fuelPreference is the newer column and
          // answer_2 the old flat field, kept as fallbacks.
          // answers.fuel_type = the onboarding answer and what Profile's change
          // (user/save-answers) writes. fuelPreference only as a fallback: the
          // server sets it to the default 'petrol' for every user.
          apply(
            profile?.answers?.fuel_type ||
              profile?.fuelPreference ||
              profile?.answer_2,
          );
        } catch (e) {
          // Keep what we have
        }
      })();

      return () => {
        active = false;
      };
    }, [dispatch]),
  );

  return fuelType;
};